import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { forwardRef } from 'react';

import { cn } from './cn';
import { useReturnFocus } from './useReturnFocus';

/**
 * Floating panels that portal to `document.body` on their own (the food details
 * popup, the push question dialog) sit outside any open dialog. They mark
 * themselves with `data-detached-overlay` so clicking them does not dismiss it.
 */
const isDetachedOverlayEvent = (event: Event) =>
  event.target instanceof Element && !!event.target.closest('[data-detached-overlay]');

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-overlay bg-ink/45 backdrop-blur-xs', className)}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /** Hide the default corner close control (e.g. pause overlays that require an explicit action). */
  hideClose?: boolean;
};

export const DialogContent = forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  ({ children, className, onInteractOutside, onOpenAutoFocus, onCloseAutoFocus, hideClose, ...props }, ref) => {
    const { handlers, capture } = useReturnFocus({
      onOpenAutoFocus,
      onCloseAutoFocus,
      onInteractOutside: (event: Parameters<NonNullable<typeof onInteractOutside>>[0]) => {
        if (isDetachedOverlayEvent(event)) {
          event.preventDefault();
        }
        onInteractOutside?.(event);
      },
    });
    return (
      <DialogPrimitive.Portal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={ref}
          {...handlers}
          className={cn(
            'fixed left-1/2 top-1/2 z-overlay grid max-h-dialog w-full max-w-lg -translate-x-1/2 -translate-y-1/2',
            'gap-4 overflow-y-auto overscroll-contain rounded-auditor-lg border border-line bg-surface p-6 text-ink shadow-auditor-overlay',
            'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
            className,
          )}
          {...props}
        >
          {capture}
          {children}
          {!hideClose && (
            <DialogPrimitive.Close
              className={cn(
                'absolute right-4 top-4 rounded-auditor p-1 text-ink-muted transition-colors hover:bg-canvas hover:text-ink',
                'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand disabled:pointer-events-none',
              )}
            >
              <X aria-hidden="true" className="size-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

export const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-1.5 text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

export const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

export const DialogTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn('m-0 text-lg font-semibold tracking-title', className)} {...props} />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

export const DialogDescription = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('m-0 text-sm text-ink-muted', className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
